import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import useUserPermissions from '../../../hooks/useUserPermissions';
import { formatCurrency, formatPercent, formatTimeAgo, formatSOL } from '../../../utils/formatters';
import StatCard from '../../common/StatCard/StatCard';
import UserSelector from '../../common/UserSelector';
import SniperTradeHistory from './SniperTradeHistory';
import { showConfirm, showSuccess, showError } from '../../../utils/sweetAlertHelper';
import './SniperDashboard.css';

const API_BASE_URL = import.meta.env.VITE_API_URL ;

export default function SniperDashboard() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { isAdmin } = useUserPermissions();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [positions, setPositions] = useState([]);
  const [stats, setStats] = useState(null);
  const [targetUserId, setTargetUserId] = useState(user?._id);
  
  useEffect(() => {
    if (user?._id && !targetUserId) setTargetUserId(user._id);
  }, [user]);
  
  useEffect(() => {
    if (!targetUserId) return;
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [targetUserId]);
  
  const fetchData = async () => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` }
      };
      
      const params = targetUserId !== user?._id ? `?userId=${targetUserId}` : '';
      
      const [statusRes, positionsRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/sniper/status${params}`, config),
        axios.get(`${API_BASE_URL}/sniper/positions${params}`, config),
        axios.get(`${API_BASE_URL}/sniper/stats${params}`, config)
      ]);
      
      setStatus(statusRes.data.data);
      setPositions(positionsRes.data.data?.positions || []);
      setStats(statsRes.data.data);
    } catch (err) {
      console.error('Error fetching sniper data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="sniper-dashboard-pro">
        <div className="loading-center">
          <div className="spinner-large"></div>
          <p>Loading Sniper Bot...</p>
        </div>
      </div>
    );
  }
  
  if (!status?.configured) {
    return (
      <div className="sniper-dashboard-pro">
        <div className="empty-center">
          <h2>🐋 Solana Sniper Not Configured</h2>
          <p>Configure your Solana wallet to start whale tracking</p>
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => navigate('/sniper/config')}>
              Configure Now →
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="sniper-dashboard-pro">
      {/* Page Header */}
      <div className="page-header-pro">
        <div className="header-left">
          <h1 className="page-title-pro">🐋 Solana Sniper Bot</h1>
        </div>
        <div className="header-right">
          {isAdmin && (
            <UserSelector
              currentUser={user}
              selectedUserId={targetUserId}
              onUserChange={setTargetUserId}
            />
          )}
          {isAdmin && (
            <button className="btn btn-secondary" onClick={() => navigate('/sniper/config')}>
              ⚙️ Configure
            </button>
          )}
        </div>
      </div>

      {/* Status Bar */}
      <div className="status-bar-pro">
        <div className="status-item">
          <span className={`status-dot ${status.enabled && status.isRunning ? 'running' : 'stopped'}`}></span>
          <span className="status-text">{status.enabled && status.isRunning ? 'Bot Active' : 'Bot Inactive'}</span>
        </div>
        <div className="status-item">
          <span className="status-label">Wallet:</span>
          <span className="status-value">{status.walletAddress?.slice(0, 8)}...{status.walletAddress?.slice(-6)}</span>
        </div>
        <div className="status-item">
          <span className="status-label">SOL Balance:</span>
          <span className="status-value">{status.solBalance?.toFixed(4)} SOL</span>
        </div>
      </div>

      {/* Main Stats */}
      <div className="stats-row-pro">
        <StatCard
          title="Active Positions"
          value={positions.length.toString()}
          icon="💼"
        />
        <StatCard
          title="Total PnL"
          value={formatCurrency(stats?.totalPnL || 0)}
          trend={stats?.totalPnL >= 0 ? 'up' : 'down'}
          icon="💰"
        />
        <StatCard
          title="Win Rate"
          value={formatPercent(stats?.winRate || 0, 1, false)}
          icon="🎯"
        />
        <StatCard
          title="Total Trades"
          value={stats?.totalTrades?.toString() || '0'}
          icon="🔄"
        />
      </div>

      {/* Active Positions */}
      <div className="card-pro card-pro-large">
        <div className="card-header-pro">
          <h3>💼 Active Positions ({positions.length})</h3>
        </div>
        <div className="card-body-pro">
          {positions.length === 0 ? (
            <div className="empty-state-inline">
              <p>No active positions</p>
              <span>Waiting for whale signals...</span>
            </div>
          ) : (
            <div className="positions-grid-pro">
              {positions.map(pos => (
                <div key={pos.tokenAddress} className="position-card-pro">
                  <div className="position-header-pro">
                    <a
                      href={`https://photon-sol.tinyastro.io/en/lp/${pos.tokenAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="token-link-pro"
                    >
                      <span className="token-symbol">{pos.tokenSymbol || pos.tokenAddress.slice(0, 8)}</span>
                    </a>
                    <span className={`phase-badge-pro ${pos.phase?.toLowerCase()}`}>
                      {pos.phase}
                    </span>
                  </div>

                  <div className="position-prices-pro">
                    <div className="price-item">
                      <span>ENTRY</span>
                      <strong>{formatSOL(pos.entryPrice, 12)}</strong>
                    </div>
                    <div className="price-item">
                      <span>CURRENT</span>
                      <strong>{formatSOL(pos.currentPrice, 12)}</strong>
                    </div>
                  </div>

                  <div className={`position-pnl-pro ${pos.pnlPercent >= 0 ? 'profit' : 'loss'}`}>
                    <div className="pnl-percent">{formatPercent(pos.pnlPercent || 0)}</div>
                    <div className="pnl-sol">{formatSOL(pos.pnlSol || 0, 6)}</div>
                  </div>

                  {/* Whale Supporters Info */}
                  {pos.supporters && pos.supporters.length > 0 && (
                    <div className="whale-info-pro">
                      <div className="whale-badges">
                        {pos.supporters.map((supporter, i) => (
                          <a
                            key={i}
                            href={`https://solscan.io/account/${supporter.wallet}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="whale-badge"
                            title={`View ${supporter.name} on Solscan`}
                          >
                            {supporter.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Position Details: Entry Time & Amount */}
                  <div className="position-details-pro">
                    <div className="detail-item">
                      <span className="detail-label">📅 Comprado</span>
                      <span className="detail-value">{formatTimeAgo(pos.entryTime)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">💰 Cantidad</span>
                      <span className="detail-value">{pos.tokenAmount?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>

                  {pos.dcaCount > 0 && (
                    <div className="dca-badge-pro">
                      📊 DCA: {pos.dcaCount}x
                    </div>
                  )}

                  <div className="position-actions-pro">
                    <button
                      className="btn btn-danger btn-sm btn-block"
                      onClick={() => handleSellPosition(pos.tokenAddress, pos.tokenSymbol)}
                    >
                      🔴 Sell Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Trade History - Full Width */}
      <div className="card-pro card-pro-large">
        <div className="card-header-pro">
          <h3>📜 Trade History</h3>
        </div>
        <div className="card-body-pro" style={{ padding: 0 }}>
          <SniperTradeHistory userId={targetUserId} currentUser={user} />
        </div>
      </div>
    </div>
  );

  async function handleSellPosition(tokenAddress, tokenSymbol) {
    const result = await showConfirm(
      `Se venderá el 100% de la posición de ${tokenSymbol}.`,
      '¿Vender toda la posición?',
      { confirmButtonText: 'Sí, vender ahora' }
    );
    
    if (!result.isConfirmed) return;
    
    try {
      await axios.post(
        `${API_BASE_URL}/sniper/sell/${tokenAddress}`,
        {
          percentage: 100,
          userId: targetUserId
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` } }
      );
      
      await showSuccess('La orden de venta ha sido ejecutada correctamente.', '✅ Venta Ejecutada');
      fetchData();
    } catch (err) {
      await showError(err.response?.data?.error || err.message, 'Error en la Venta');
    }
  }
}
